import { z } from 'zod';
import { FleetQuestionAnswerChannelSchema, FleetQuestionOptionSchema } from './fleet-questions.schemas';

/**
 * The fleet assistant's conversation (IMM-1420): a fleet person writes to it, any approved fleet machine takes the
 * message as a turn and answers it. One conversation per fleet person.
 */
export const FLEET_ASSISTANT_TEXT_MAX_CHARS = 4_000;
export const FLEET_ASSISTANT_HISTORY_DEPTH = 40;
/** How long a pending turn may wait for a machine to take it. */
export const FLEET_ASSISTANT_TURN_TAKE_MS = 2 * 60 * 1000;
/** How long a machine that took a turn has to answer it. */
export const FLEET_ASSISTANT_TURN_REPLY_MS = 10 * 60 * 1000;
export const FLEET_ASSISTANT_OPEN_TICKETS_MAX = 30;

export const FLEET_ASSISTANT_MESSAGE_AUTHORS = ['operator', 'assistant'] as const;
export type FleetAssistantMessageAuthor = (typeof FLEET_ASSISTANT_MESSAGE_AUTHORS)[number];

export const FLEET_ASSISTANT_CONTEXT_SOURCES = ['quote', 'session', 'panel', 'earlier-quote', 'assistant'] as const;
export const FleetAssistantContextSourceSchema = z.enum(FLEET_ASSISTANT_CONTEXT_SOURCES);
export type FleetAssistantContextSource = z.infer<typeof FleetAssistantContextSourceSchema>;

export const FLEET_ASSISTANT_TURN_STATES = ['pending', 'leased', 'answered', 'failed'] as const;
export type FleetAssistantTurnState = (typeof FLEET_ASSISTANT_TURN_STATES)[number];

export const FleetAssistantMessageBodySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: z.string() }),
  z.object({ kind: z.literal('asks-for-ticket'), tickets: z.array(z.string()) }),
  z.object({ kind: z.literal('refused') }),
  z.object({ kind: z.literal('answer-recorded'), ticket: z.string(), answer: z.string() }),
  z.object({ kind: z.literal('already-answered'), ticket: z.string(), answer: z.string(), answeredBy: z.string() }),
  z.object({ kind: z.literal('failed') }),
]);
export type FleetAssistantMessageBody = z.infer<typeof FleetAssistantMessageBodySchema>;

export const FleetAssistantMessageViewSchema = z.object({
  id: z.uuid(),
  author: z.enum(FLEET_ASSISTANT_MESSAGE_AUTHORS),
  /** The fleet person's name on an operator message; null on the assistant's. */
  authorName: z.string().nullable(),
  body: FleetAssistantMessageBodySchema,
  /** Where it was written (operator) or delivered (assistant); null is the fleet page. */
  channel: FleetQuestionAnswerChannelSchema.nullable(),
  context: z.object({ questionId: z.uuid().nullable(), ticket: z.string().nullable() }),
  createdAt: z.iso.datetime(),
});
export type FleetAssistantMessageView = z.infer<typeof FleetAssistantMessageViewSchema>;

export const FleetAssistantContextQuestionSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  ticket: z.string(),
  problem: z.string(),
  brief: z.string().nullable(),
  options: z.array(FleetQuestionOptionSchema),
  askedAt: z.iso.datetime(),
  answer: z
    .object({
      text: z.string(),
      answeredBy: z.string(),
      answeredAt: z.iso.datetime(),
      answeredVia: FleetQuestionAnswerChannelSchema.nullable(),
    })
    .nullable(),
  /** A later round claimed its key: it can no longer be answered. */
  moot: z.boolean(),
});
export type FleetAssistantContextQuestion = z.infer<typeof FleetAssistantContextQuestionSchema>;

export const FleetAssistantTurnSchema = z.object({
  messageId: z.uuid(),
  person: z.object({ adminId: z.uuid(), name: z.string() }),
  channel: FleetQuestionAnswerChannelSchema.nullable(),
  text: z.string(),
  receivedAt: z.iso.datetime(),
  context: z
    .object({ source: FleetAssistantContextSourceSchema, question: FleetAssistantContextQuestionSchema })
    .nullable(),
  history: z.array(FleetAssistantMessageViewSchema),
  openTickets: z.array(z.string()),
  replyBy: z.iso.datetime(),
});
export type FleetAssistantTurn = z.infer<typeof FleetAssistantTurnSchema>;

export const TakeFleetAssistantTurnBodySchema = z.object({});
export const TakeFleetAssistantTurnResponseSchema = z.object({ turn: FleetAssistantTurnSchema.nullable() });
export type TakeFleetAssistantTurnResponse = z.infer<typeof TakeFleetAssistantTurnResponseSchema>;

export const FleetAssistantTurnPathParamsSchema = z.object({ messageId: z.uuid() });

export const ReplyToFleetAssistantTurnBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('text'),
    text: z.string().trim().min(1).max(FLEET_ASSISTANT_TEXT_MAX_CHARS),
    questionId: z.uuid().nullable().optional(),
    ticket: z.string().trim().min(1).max(32).nullable().optional(),
  }),
  z.object({ kind: z.literal('asks-for-ticket') }),
  z.object({ kind: z.literal('refused') }),
  z.object({ kind: z.literal('failed') }),
]);
export type ReplyToFleetAssistantTurnBody = z.infer<typeof ReplyToFleetAssistantTurnBodySchema>;

export const ReplyToFleetAssistantTurnResponseSchema = z.object({ id: z.uuid(), createdAt: z.iso.datetime() });
export type ReplyToFleetAssistantTurnResponse = z.infer<typeof ReplyToFleetAssistantTurnResponseSchema>;

export const FLEET_ASSISTANT_PAGE_SIZE = 100;

export const ListFleetAssistantMessagesResponseSchema = z.object({
  data: z.array(FleetAssistantMessageViewSchema),
  /** A turn of this conversation is waiting for, or being answered by, the assistant. */
  pending: z.boolean(),
});
export type ListFleetAssistantMessagesResponse = z.infer<typeof ListFleetAssistantMessagesResponseSchema>;

export const ListFleetAssistantMessagesQuerySchema = z.object({ questionId: z.uuid().optional() });

export const PostFleetAssistantMessageBodySchema = z.object({
  text: z.string().trim().min(1).max(FLEET_ASSISTANT_TEXT_MAX_CHARS),
  /** Written under this question in the ticket panel: it is the message's context, like a quote (IMM-1420). */
  questionId: z.uuid().optional(),
});
export type PostFleetAssistantMessageBody = z.infer<typeof PostFleetAssistantMessageBodySchema>;
